import React from 'react';
import { CheckCircle, AlertCircle, Flag, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VerificationQueue = ({
  unverifiedPosts,
  loading,
  verifying,
  deleting,
  reporting,
  onVerify,
  onDelete,
  onReport
}) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#FF8C00] border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading posts...</p>
      </div>
    );
  }

  if (unverifiedPosts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center" data-testid="no-unverified-posts">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <p className="text-xl text-gray-700 mb-2">All caught up!</p>
        <p className="text-gray-500">There are no unverified posts at the moment.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="unverified-posts-list">
      <div className="flex items-center space-x-2 text-yellow-700 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <AlertCircle className="w-5 h-5" />
        <span className="font-medium">
          {unverifiedPosts.length} {unverifiedPosts.length === 1 ? 'post' : 'posts'} pending verification
        </span>
      </div>

      {unverifiedPosts.map((post) => (
        <div
          key={post.id}
          className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[#FF8C00]"
          data-testid="unverified-post-item"
        >
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-xs px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-700">
                  {post.post_type}
                </span>
                <span className="text-xs text-yellow-600 font-medium">UNVERIFIED</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-testid="post-title">
                {post.title}
              </h3>
              <p className="text-gray-600 mb-3" data-testid="post-body">
                {post.body}
              </p>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span data-testid="post-author">By {post.author_screenname}</span>
                <span>•</span>
                <span data-testid="post-date">
                  {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                </span>
                <span>•</span>
                <span>{post.comment_count || 0} comments</span>
              </div>
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-1 bg-[#FFF4E6] text-[#FF8C00] rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Display images if present */}
              {post.images && post.images.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {post.images.map((imageUrl) => (
                    <img
                      key={imageUrl}
                      src={`${BACKEND_URL}${imageUrl}`}
                      alt={`Post image ${imageUrl.split('/').pop()}`}
                      className="rounded-lg w-full h-48 object-cover"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => onVerify(post.id)}
              disabled={verifying[post.id]}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="verify-post-button"
            >
              <CheckCircle className="w-4 h-4" />
              <span>{verifying[post.id] ? 'Verifying...' : 'Mark as Verified'}</span>
            </button>
            
            <button
              onClick={() => onReport(post.id)}
              disabled={reporting[post.id]}
              className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="report-post-button"
            >
              <Flag className="w-4 h-4" />
              <span>{reporting[post.id] ? 'Reporting...' : 'Report'}</span>
            </button>

            <button
              onClick={() => onDelete(post.id)}
              disabled={deleting[post.id]}
              className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="delete-post-button"
            >
              <Trash2 className="w-4 h-4" />
              <span>{deleting[post.id] ? 'Deleting...' : 'Delete'}</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default VerificationQueue;
